import { useAuth } from "@clerk/clerk-react";
import { Button } from "Components/ui/button";
import { Separator } from "Components/ui/separator";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "hooks/ThemeContext";
import UseInnerWidth from "hooks/UseInnerWidth";
import {
  Calendar,
  Codepen,
  LayoutDashboard,
  LogOut,
  MessageCircleQuestionIcon,
  ScrollText,
  Settings,
  Wallet,
  X,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import LightLogo from "../../Assets/Images/LightLogo.svg";
import mt5Icon from "../../Assets/Images/mt5icon.svg";
import RfLogo from "../../Assets/Images/RfLogo.svg";
import TradeLockerBlack from "../../Assets/Images/TradeLockerBlack.svg";
import tradelockerIcon from "../../Assets/Images/tradelockerIcon.svg";
import { getChallengesRequest } from "../../Redux/ChallengeMain/ChallengeMainActions";
import "./SideNavBar.scss";

function SideNavBar({ open, setOpen }) {
  const [childrenData, setChildrenData] = useState([]);
  const { theme, toggleTheme } = useTheme();

  const { getToken } = useAuth();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getChallengesRequest(getToken));
  }, []);

  const { challenges, getChallengesLoading } = useSelector(
    (state) => state.challengeMainReducer
  );

  const [data, setData] = useState([]);

  useEffect(() => {
    if (!getChallengesLoading) {
      setData([
        {
          title: "Dashboard",
          icon: <LayoutDashboard />,
          route: "/",
        },
        {
          title: "Wallet",
          icon: <Wallet />,
          route: "/wallet",
        },

        {
          title: "Challenges",
          icon: <Codepen />,
          route: "/challenge",
          Children: challenges?.map((item, index) => {
            return {
              title: "CH " + item?.login,
              route: `/challenge/${item?.challengeId}/overview`,
              platform: item?.platform,
            };
          }),
        },
        {
          title: "Certificates",
          icon: <ScrollText />,
          route: "/certificates",
        },
        {
          title: "Economic Calendar",
          icon: <Calendar />,
          route: "/calendar",
        },
        {
          title: "FAQs",
          icon: <MessageCircleQuestionIcon />,
        },
      ]);
    }
  }, [challenges]);

  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedChildren, setSelectedChildren] = useState();

  useEffect(() => {
    setSelectedChildren();
  }, [selectedTab]);

  const width = UseInnerWidth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (pathname?.includes("/challenge")) {
      setSelectedTab(2);
      setSelectedChildren(
        data[2]?.Children?.findIndex(
          (item) => pathname.split("/")[2] === item.route.split("/")[2]
        )
      );
    } else if (pathname.includes("/landing")) {
      setSelectedTab();
      setSelectedChildren();
    } else if (pathname.includes("/wallet")) {
      setSelectedTab(1);
    } else if (pathname.includes("/economicCalendar")) {
      setSelectedTab(3);
    } else if (pathname === "/") {
      setSelectedTab(0);
    } else if (pathname.includes("profile")) {
      setSelectedTab(data?.length);
    }
  }, [pathname]);

  const { signOut } = useAuth();

  const handleSignout = () => {
    signOut();
  };

  return (
    <div className="relative">
      <motion.div
        className="absolute z-[50] bg-[rgba(0,0,0,0.5)] backdrop-blur-[10px] h-[100dvh] w-[100dvw]"
        initial={{ opacity: 0, display: "none" }}
        animate={{
          opacity: open ? 1 : 0,
          display: width < 1024 ? (open ? "block" : "none") : "none",
        }}
        transition={{
          damping: 20,
          stiffness: 100,
          type: "spring",
        }}
        onClick={() => {
          setOpen(false);
        }}
      ></motion.div>
      <motion.div
        className={`sm:w-[17rem] w-[100dvw] h-full z-[100] flex flex-col bg-background ${
          width < 1024 ? "absolute " : "relative"
        }`}
        initial={{ x: width < 1024 ? "-100%" : "0" }}
        animate={{ x: width < 1024 ? (open ? "0" : "-100%") : "0" }}
        transition={{
          damping: 20,
          stiffness: 100,
          type: "spring",
        }}
      >
        <div className="flex items-center justify-between border-b-2 border-border-darkNew p-6 cursor-pointer select-none">
          <img
            src={theme === "light" ? LightLogo : RfLogo}
            alt="Logo"
            className="w-[9rem]"
            onClick={() => {
              navigate("/");
            }}
          />
          {width < 1024 && <X size={"1.1rem"} onClick={() => setOpen(false)} />}
        </div>
        <div className="flex-1 p-6 overflow-auto">
          <Button
            className="bg-primary-red w-full"
            onClick={() => {
              navigate("/landing");
              setSelectedTab();
              setSelectedChildren();
            }}
          >
            New Challenge
          </Button>
          <div className="mt-16">
            <div className="text-white-opacity mb-6">MENU</div>
            <div className="flex flex-col gap-2">
              {data.map((item, index) => {
                return (
                  <div
                    key={index}
                    onClick={() => {
                      if (item?.title == "Challenges") {
                        if (!item?.Children?.length) return;
                      } else
                        item.route
                          ? navigate(item.route)
                          : item.title === "FAQs" && window.open("https://intercom.help/onlyfundsfx/en", "_blank");
                    }}
                  >
                    <div
                      className={`cursor-pointer p-3 rounded-lg flex items-center gap-2 ${
                        selectedTab === index
                          ? "bg-background-secondaryOpacity text-primary-red"
                          : "text-white-opacity"
                      }`}
                      onClick={() => {
                         item.title !== "FAQs" &&
                        setSelectedTab(index);
                      }}
                    >
                      {item.icon}
                      <div>{item.title}</div>
                    </div>
                    <AnimatePresence>
                      {item?.Children && selectedTab === index && (
                        <motion.div
                          className="overflow-hidden pl-6 mt-2"
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          onClick={() => setSelectedTab(index)}
                        >
                          {getChallengesLoading ? (
                            <div
                              className={` border-l ${
                                theme === "light"
                                  ? "border-black-opacity text-black-opacity"
                                  : "border-border-grey text-white-opacity"
                              } cursor-pointer px-6 py-2 flex items-center gap-2 `}
                            >
                              Loading...
                            </div>
                          ) : !item?.Children?.length ? (
                            <div
                              className="text-white-opacity hover:text-primary underline border-l border-border-grey cursor-pointer px-6 py-2 flex items-center gap-2"
                              onClick={() => {
                                navigate("/landing");
                              }}
                            >
                              Create Challenge!
                            </div>
                          ) : (
                            item.Children.map((child, index) => {
                              return (
                                <div
                                  key={index}
                                  className={`border-l ${
                                    theme === "light"
                                      ? "border-black-opacity"
                                      : "border-border-grey"
                                  } cursor-pointer pl-6 py-2 flex items-center gap-2 ${
                                    selectedChildren === index
                                      ? "text-primary"
                                      : `${
                                          theme === "light"
                                            ? "text-black-opacity"
                                            : "text-white-opacity"
                                        }`
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedChildren(index);
                                    navigate(child.route);
                                  }}
                                >
                                  {child.platform.toLowerCase() ===
                                  "platform 5" ? (
                                    <img
                                      src={mt5Icon}
                                      alt="mt5Icon"
                                      className="w-[0.7rem] mt-[-0.1rem]"
                                    />
                                  ) : (
                                    <img
                                      src={
                                        theme === "light"
                                          ? TradeLockerBlack
                                          : tradelockerIcon
                                      }
                                      alt="tradelockerIcon"
                                      className="w-[0.7rem] mt-[-0.1rem]"
                                    />
                                  )}
                                  <div className={`text-base `}>
                                    {child.title}
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
            <Separator className="bg-border-darkNew mb-6 mt-12" />

            <div
              className={`mb-2 cursor-pointer p-3 rounded-lg flex items-center gap-2 ${
                selectedTab === data?.length
                  ? "bg-background-secondaryOpacity text-primary-red"
                  : "text-white-opacity"
              }`}
              onClick={() => {
                setSelectedTab(data?.length);
                navigate("profile?tab=0");
              }}
            >
              <Settings />
              <div>Profile</div>
            </div>

            <div
              className={`mb-2 cursor-pointer p-3 rounded-lg flex items-center gap-2 text-primary`}
              onClick={handleSignout}
            >
              <LogOut />
              <div>Logout</div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default SideNavBar;
